import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ChevronUp, ChevronDown, Filter, EyeOff, GripVertical, Info, ChevronsUpDown } from 'lucide-react';
import ColumnExplanationTooltip from './ColumnExplanationTooltip';
import { sortData, formatColumnName, formatDate, formatNumber } from './dataProcessing';
import FilterModal from './FilterModal';
import { useCompanyNews } from '../../hooks/useCompanyNews';
import NewsIndicator from '../NewsIndicator'; 
import _ from 'lodash';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import HiddenColumnsChips from './HiddenColumnsChips';
import { currentConfig } from '../../contexts/ConfigContext';


const TableView = ({ 
  data,
  originalData,
  schema, 
  initialSortColumn, 
  initialSortOrder, 
  onTextSelection,
  visibleColumns = [],
  columnOrder = [],
  onSortChange,
  enableFiltering = true,
  onFilterChange,
  activeFilters = {},
  onProcessedDataChange,
  showSummary = false,
  query,
  onColumnOrderChange,
  onVisibleColumnsChange,
  isNumberFormatted = true
}) => {
  const [sortConfig, setSortConfig] = useState({ 
    key: initialSortColumn || null, 
    direction: initialSortOrder || 'asc' 
  });
  const [tableHeight, setTableHeight] = useState('auto');
  const [tooltipContent, setTooltipContent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ left: 0, width: 0 });
  const { isTrackedColumn } = useCompanyNews();
  const [showRowCount, setShowRowCount] = useState(false);
  const [visibleRowsCount, setVisibleRowsCount] = useState(0);
  const rowHeight = 40; // Standard row height in pixels
  const [filterModalState, setFilterModalState] = useState({
    isOpen: false,
    columnName: null,
    position: { top: 0, left: 0 }
  });
  
  const headerRef = useRef(null);
  const tableRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  const columnExplanations = useMemo(() => {
    if (!query?.explanation?.logic_by_column) return {};
    return query.explanation.logic_by_column;
  }, [query]);


  // Use columnOrder if provided, otherwise fall back to schema fields
  const displayColumns = useMemo(() => {
    if (columnOrder.length > 0) {
      return columnOrder.filter(col => visibleColumns.includes(col));
    }
    return schema?.fields?.map(field => field.name) || [];
  }, [columnOrder, visibleColumns, schema]);

  // Handle table height for scrolling
  useEffect(() => {
    if (headerRef.current && data.length > 7) {
      const headerHeight = headerRef.current.offsetHeight;
      const rowHeight = 40; // Standard row height
      setTableHeight(`${headerHeight + (rowHeight * 7)}px`);
    } else {
      setTableHeight('auto');
    }
  }, [data]);

  // Update sort config when initial values change
  useEffect(() => {
    setSortConfig({ key: initialSortColumn, direction: initialSortOrder });
  }, [initialSortColumn, initialSortOrder]);

  // New function to handle column hiding
  const handleHideColumn = useCallback((columnName) => {
    const newVisibleColumns = visibleColumns.filter(col => col !== columnName);
    if (newVisibleColumns.length > 0) { // Ensure at least one column remains visible
      onVisibleColumnsChange?.(newVisibleColumns);
    }
  }, [visibleColumns, onVisibleColumnsChange]);

  // New function to handle drag end
  const handleDragEnd = useCallback((result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    
    // Work with the full columnOrder array
    const items = Array.from(columnOrder);
    
    // Get the actual columns being displayed
    const visibleItems = items.filter(col => visibleColumns.includes(col));
    
    // Get the column being moved
    const [movedColumn] = visibleItems.splice(result.source.index, 1);
    visibleItems.splice(result.destination.index, 0, movedColumn);
    
    // Create new column order by preserving hidden columns in their positions
    const newColumnOrder = items.reduce((acc, col) => {
      if (!visibleColumns.includes(col)) {
        // Keep hidden columns in their original positions
        acc.push(col);
      }
      return acc;
    }, []);
  
    // Insert visible columns in their new order
    visibleItems.forEach((col, index) => {
      newColumnOrder.splice(index, 0, col);
    });
  
    // Use requestAnimationFrame to ensure smooth animation completion
    requestAnimationFrame(() => {
      onColumnOrderChange?.(newColumnOrder);
    });
  }, [columnOrder, visibleColumns, onColumnOrderChange]);

    const getDragStyle = useCallback((isDragging, draggableStyle) => ({
      ...draggableStyle,
      transform: draggableStyle.transform 
        ? draggableStyle.transform.replace(/translate\(([^,]+),([^)]+)\)/, (_, x, y) => `translate(${x}, 0px)`)
        : 'translate(0, 0)',
      transition: isDragging ? 'transform 0.01s linear' : 'transform 0.2s ease-in-out',
      zIndex: isDragging ? 9999 : draggableStyle.zIndex,
      cursor: isDragging ? 'grabbing' : 'grab',
      left: 'auto'
    }), []);

    const isNumericColumn = useCallback((columnName) => {
      const field = schema?.fields?.find(f => f.name === columnName);
      // Only check schema type
      return field && ['INTEGER', 'FLOAT', 'NUMERIC'].includes(field.type);
    }, [schema]);

    // Process data with filters and sorting
    const processedData = useMemo(() => {
      if (!Array.isArray(data) || data.length === 0) return [];
      
      // Apply filters first if filtering is enabled
      let filteredData = data;
      if (enableFiltering && Object.keys(activeFilters).length > 0) {
        // Optional backwards compatibility for old string filters
        const normFilters = {};
        Object.entries(activeFilters).forEach(([col, arr]) => {
          normFilters[col] = arr.map(v =>
            typeof v === 'string' && isNumericColumn(col) ? 
              parseFloat(v.replace(/[^0-9.-]+/g,'')) : v
          );
        });
        
        filteredData = data.filter(row => {
          return Object.entries(normFilters).every(([columnName, allowedValues]) => {
            const cell = row[columnName];
            if (!cell) return false;
            
            // For numeric columns, compare with sortValue
            if (typeof cell.sortValue === 'number') {
              return allowedValues.includes(cell.sortValue);
            } else {
              // For string columns, compare with displayValue
              const stringValue = (cell.displayValue ?? cell) === null || 
                                (cell.displayValue ?? cell) === undefined 
                ? 'N/A' 
                : String(cell.displayValue ?? cell);
              return allowedValues.includes(stringValue);
            }
          });
        });
      }

      // Then apply sorting
      const sortedData = sortData(filteredData, sortConfig.key, sortConfig.direction);

      // Notify parent component of processed data if callback provided
      if (onProcessedDataChange) {
        onProcessedDataChange(sortedData);
      }

      return sortedData;
    }, [data, activeFilters, sortConfig, enableFiltering, onProcessedDataChange, isNumericColumn]);


const getFilteredDataForColumn = useCallback((columnName) => {
  if (!Array.isArray(data) || data.length === 0) return [];
  
  // Apply all filters except the current column's filter
  const otherFilters = Object.entries(activeFilters).filter(([key]) => key !== columnName);
  
  if (otherFilters.length === 0) return data;
  
  // Optional backwards compatibility
  const normFilters = {};
  otherFilters.forEach(([col, arr]) => {
    normFilters[col] = arr.map(v =>
      typeof v === 'string' && isNumericColumn(col) ? 
        parseFloat(v.replace(/[^0-9.-]+/g,'')) : v
    );
  });
  
  return data.filter(row => {
    return Object.entries(normFilters).every(([filterCol, allowedValues]) => {
      const cell = row[filterCol];
      if (!cell) return false;
      
      // For numeric columns, compare with sortValue
      if (typeof cell.sortValue === 'number') {
        return allowedValues.includes(cell.sortValue);
      } else {
        // For string columns, compare with displayValue
        const stringValue = (cell.displayValue ?? cell) === null || 
                          (cell.displayValue ?? cell) === undefined 
          ? 'N/A' 
          : String(cell.displayValue ?? cell);
        return allowedValues.includes(stringValue);
      }
    });
  });
}, [data, activeFilters, isNumericColumn]);

  // Handle column sort request
  const requestSort = useCallback((key, forceDesc = false) => {
    let direction = 'asc';
    
    if (forceDesc) {
      // If forcing descending, set to desc unless it's already the active sort
      direction = (sortConfig.key === key && sortConfig.direction === 'desc') ? null : 'desc';
    } else {
      // For ascending, set to asc unless it's already the active sort
      direction = (sortConfig.key === key && sortConfig.direction === 'asc') ? null : 'asc';
    }
    
    if (direction === null) {
      // Clear sorting
      setSortConfig({ key: null, direction: 'asc' });
      if (onSortChange) {
        onSortChange(null, 'asc');
      }
    } else {
      setSortConfig({ key, direction });
      if (onSortChange) {
        onSortChange(key, direction);
      }
    }
  }, [sortConfig, onSortChange]);

  // Handle filter icon click
  const handleFilterClick = useCallback((e, columnName) => {
    e.stopPropagation();
    const headerRect = e.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const modalWidth = 300; // Width of the modal
    const modalHeight = 500; // Max height of the modal
    
    // Calculate initial position
    let left = headerRect.left + window.scrollX;
    let top = headerRect.bottom + window.scrollY;
  
    // Adjust horizontal position if modal would overflow viewport
    if (left + modalWidth > viewportWidth) {
      left = Math.max(0, viewportWidth - modalWidth - 20); // 20px padding from right edge
    }
  
    // Adjust vertical position if modal would overflow viewport
    if (top + modalHeight > viewportHeight) {
      // Position above the header if there's more space available
      if (headerRect.top > modalHeight) {
        top = headerRect.top - modalHeight + window.scrollY;
      } else {
        // If not enough space above, position at top of viewport with small padding
        top = window.scrollY + 20;
      }
    }
  
    setFilterModalState({
      isOpen: true,
      columnName,
      position: { top, left }
    });
  }, []);

  // Handle filter application
  const handleApplyFilter = useCallback((columnName, selectedValues) => {
    const newFilters = { ...activeFilters };
    
    // Get the unique values for this column from the original data
    const uniqueColumnValues = _.uniq(originalData.map(row => 
      String(row[columnName]?.displayValue ?? row[columnName])
    ));
    
    if (selectedValues.length === 0 || selectedValues.length === uniqueColumnValues.length) {
      delete newFilters[columnName];
    } else {
      newFilters[columnName] = selectedValues;
    }
  
    if (onFilterChange) {
      onFilterChange(newFilters);
    }
  }, [activeFilters, originalData, onFilterChange]);


  const formatColumnNameWithBreaks = (columnName, maxLength = 18) => {
    // Convert to string first to handle numeric column names
    const columnNameStr = String(columnName);
    const formattedName = formatColumnName(columnNameStr);
    
    // If the name is short enough, return it as is
    if (formattedName.length <= maxLength) {
      return formattedName;
    }
    
    const words = formattedName.split(' ');
    
    // If it's a single word, don't break it
    if (words.length === 1) {
      return formattedName;
    }
    
    // Simple algorithm: fit words until we exceed maxLength
    let lines = [];
    let currentLine = '';
    
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const testLine = currentLine ? currentLine + ' ' + word : word;
      
      if (testLine.length <= maxLength) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
        }
        currentLine = word;
      }
    }
    
    // Don't forget the last line
    if (currentLine) {
      lines.push(currentLine);
    }
    
    // Return as a single div with line breaks
    return (
      <div style={{ lineHeight: '1.2' }}>
        {lines.map((line, index) => (
          <div key={index}>{line}</div>
        ))}
      </div>
    );
  };
  
  const handleShowColumn = useCallback((columnName) => {
    const newVisibleColumns = [...visibleColumns, columnName];
    onVisibleColumnsChange?.(newVisibleColumns);
  }, [visibleColumns, onVisibleColumnsChange]);

  // Close filter modal
  const closeFilterModal = useCallback(() => {
    setFilterModalState(prev => ({ ...prev, isOpen: false }));
  }, []);

    const isStringColumn = useCallback((columnName) => {
      const field = schema?.fields?.find(f => f.name === columnName);
      return field && ['STRING'].includes(field.type);
    }, [schema]);

    const calculateColumnStats = useCallback((columnName) => {
      const field = schema?.fields?.find(f => f.name === columnName);
      if (!field) return null;
    
      // Convert columnName to string for comparison
      const columnNameStr = String(columnName);
    
      // Check if this column is exempted from formatting
      const exemptedColumns = currentConfig?.processing?.exemptedColumns || [];
      const isExempted = exemptedColumns.some(exemptCol => {
        // Ensure both values are strings before comparison
        const exemptColStr = String(exemptCol);
        return columnNameStr.toLowerCase().includes(exemptColStr.toLowerCase());
      });
    
      // If exempted, treat as string regardless of schema type
      if (isExempted || field.type === 'STRING') {
        const values = processedData
          .map(row => row[columnName]?.displayValue)
          .filter(val => val !== null && val !== undefined);
        
        if (values.length === 0) return null;
    
        const uniqueValues = new Set(values);
        
        return {
          type: 'string',
          total: values.length,
          unique: uniqueValues.size
        };
      }
    
      if (['INTEGER', 'FLOAT', 'NUMERIC'].includes(field.type) || isNumericColumn(columnName)) {
        const values = processedData
          .map(row => {
            const value = row[columnName]?.sortValue;
            // Skip null, undefined, or NEGATIVE_INFINITY values
            if (value === null || value === undefined || value === Number.NEGATIVE_INFINITY) return NaN;
            if (typeof value === 'number') return value;
            if (typeof value === 'string') {
              const cleanValue = value.replace(/,/g, '').trim();
              return parseFloat(cleanValue);
            }
            return NaN;
          })
          .filter(val => !isNaN(val));
    
        // If no valid values remain, return null early to avoid calculations with empty array
        if (values.length === 0) return null;
        
        const sum = values.reduce((acc, val) => acc + val, 0);
        const avg = sum / values.length;
        const min = Math.min(...values);
        const max = Math.max(...values);
    
        // Format numbers conditionally based on isNumberFormatted
        const formatValue = (value) => {
          if (isNumberFormatted) {
            return formatNumber(value, columnNameStr); // Use the string version here
          }
          // When not formatting, still ensure proper decimal places
          return typeof value === 'number' ? value.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
          }) : value;
        };
    
        return {
          type: 'numeric',
          sum: formatValue(sum),
          avg: formatValue(avg),
          min: formatValue(min),
          max: formatValue(max)
        };
      }
    
      return null;
    }, [processedData, schema, isNumberFormatted]);

    const hasColumnsWithStats = useMemo(() => {
      return displayColumns.some(columnName => {
        return (isNumericColumn(columnName) || isStringColumn(columnName)) && calculateColumnStats(columnName) !== null;
      });
    }, [displayColumns, isNumericColumn, isStringColumn, calculateColumnStats]);
    
    // After calculateColumnStats, add hasNumericColumnsWithData
    const hasNumericColumnsWithData = useMemo(() => {
      return displayColumns.some(columnName => {
        const isNumeric = isNumericColumn(columnName);
        if (!isNumeric) return false;
        const stats = calculateColumnStats(columnName);
        return stats !== null;
      });
    }, [displayColumns, isNumericColumn, calculateColumnStats]);

  // Handle text selection
  const handleTextSelection = useCallback(() => {
    if (!onTextSelection) return;
    
    const selection = window.getSelection();
    const selectedText = selection.toString().trim();
    if (selectedText) {
      const range = selection.getRangeAt(0);
      const startNode = range.startContainer.parentElement;
      const endNode = range.endContainer.parentElement;
      console.log('Selection nodes:', {
        startNode,
        endNode,
        startNodeDataset: startNode.dataset,
        endNodeDataset: endNode.dataset,
        parentDataset: startNode.parentElement?.dataset
      });
      
      const selectedColumns = new Set();
      let currentNode = startNode;
      
      // Handle transposed data differently
      if (data && data.length > 0 && query && query.isTransposed) {
        // For transposed data, we want the Field value from the first column
        const row = currentNode.closest('tr');
        if (row) {
          const fieldCell = row.querySelector('td:first-child');
          if (fieldCell) {
            selectedColumns.add(fieldCell.textContent.trim());
          }
        }
      } else {
        // Original behavior for non-transposed data
        while (currentNode && currentNode !== endNode.nextSibling) {
          if (currentNode.dataset && currentNode.dataset.column) {
            selectedColumns.add(currentNode.dataset.column);
          }
          currentNode = currentNode.nextSibling || (currentNode.parentNode && currentNode.parentNode.nextSibling);
        }
      }
      
      const rect = range.getBoundingClientRect();
      const selectionData = {
        text: selectedText,
        columns: Array.from(selectedColumns),
        position: {
          x: rect.left + rect.width / 2,
          y: rect.top - 20
        },
        isTransposed: query && query.isTransposed
      };
      
      onTextSelection(selectionData);
    }
  }, [onTextSelection, query, data]); 

// Handle cell value rendering
const renderCellValue = useCallback((cellValue, columnName) => {
  if (cellValue === null || cellValue === undefined) {
    return 'N/A';
  }

  // If it's not an object (string or number), display it directly
  if (typeof cellValue !== 'object') {
    return (
      <div className="flex items-center">
        <span data-column={columnName}>{cellValue}</span>
        {isTrackedColumn(columnName) && <NewsIndicator companyName={cellValue} />}
      </div>
    );
  }

  // If it's an object with displayValue property
  if ('displayValue' in cellValue) {
    return (
      <div className="flex items-center">
        <span data-column={columnName}>{cellValue.displayValue}</span>
        {isTrackedColumn(columnName) && <NewsIndicator companyName={cellValue.displayValue} />}
      </div>
    );
  }

  // Fallback case
  const value = cellValue.value ?? String(cellValue);
  return (
    <div className="flex items-center">
      <span data-column={columnName}>{value}</span>
      {isTrackedColumn(columnName) && <NewsIndicator companyName={value} />}
    </div>
  );
}, [isTrackedColumn]);

  // Handle summary hover for tooltips
  const handleHeaderHover = useCallback((event, columnName) => {
    if (processedData.length <= 1) {
      setTooltipContent(null);
      return;
    }
  
    const stats = calculateColumnStats(columnName);
    if (stats) {
      setTooltipContent(
        <div className="p-2 text-sm">
          {stats.type === 'numeric' ? (
            <>
              <div>Sum: {stats.sum}</div>
              <div>Avg: {stats.avg}</div>
              <div>Min: {stats.min}</div>
              <div>Max: {stats.max}</div>
            </>
          ) : (
            <>
              <div>Total Values: {stats.total}</div>
              <div>Unique Values: {stats.unique}</div>
            </>
          )}
        </div>
      );
  
      const headerCell = event.currentTarget;
      const headerRect = headerCell.getBoundingClientRect();
      const tableRect = tableRef.current.getBoundingClientRect();
      
      setTooltipPosition({
        left: headerRect.left - tableRect.left,
        width: headerRect.width,
        top: headerRect.top - tableRect.top - 140  // Adjusted for integrated header
      });
    }
  }, [processedData, calculateColumnStats]);

  const handleHeaderLeave = useCallback(() => {
    setTooltipContent(null);
  }, []);

  if (!Array.isArray(data) || data.length === 0) return <div>No data available</div>;


  return (
    <div className="relative overflow-visible" ref={tableRef}>
  <div className="flex gap-2">
  <div 
    className="overflow-x-auto flex-grow relative" 
    style={{ height: tableHeight, position: 'relative' }}
    onScroll={(e) => {
        const container = e.target;
        const scrollTop = container.scrollTop;
        const viewportHeight = container.clientHeight;
        
        // Calculate which rows are currently visible
        const firstVisibleRowIndex = Math.min(
          Math.ceil(scrollTop / rowHeight) + 1,  // Add 1 for human-readable row numbers
          processedData.length // Ensure we don't exceed total rows
        );
        const numberOfVisibleRows = Math.floor(viewportHeight / rowHeight);
        const lastVisibleRowIndex = Math.min(firstVisibleRowIndex + numberOfVisibleRows - 1, processedData.length);
        
        // Format as "start-end/total"
        setVisibleRowsCount(firstVisibleRowIndex);
        setShowRowCount(true);
        
        // Clear existing timeout
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        
        scrollTimeoutRef.current = setTimeout(() => {
          setShowRowCount(false);
        }, 1000);
      }}
    >
      <div 
        className="fixed w-fit bg-gray-700 text-white px-2 py-1 rounded-full text-xs font-medium shadow-sm z-[60] flex items-center gap-1.5"
        style={{ 
          opacity: showRowCount ? 1 : 0,
          transition: 'opacity 300ms ease-in-out',
          pointerEvents: 'none',
          top: tableRef.current?.getBoundingClientRect().top + 8 + 'px',
          left: tableRef.current?.getBoundingClientRect().left + 8 + 'px'
        }}
      >
        Rows {visibleRowsCount.toLocaleString()}-{Math.min(visibleRowsCount + Math.floor(tableRef.current?.clientHeight / rowHeight) - 1, processedData.length).toLocaleString()}/{processedData.length.toLocaleString()}
      </div>
          <div className="relative min-w-full overflow-visible">
          <table className="w-full border-collapse border border-gray-300">
          {/* Controls Row - Always at the top */}
          <thead className="sticky bg-gray-50" style={{ top: 0, zIndex: 5 }}>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable 
                droppableId="table-headers" 
                direction="horizontal"
                renderClone={(provided, snapshot, rubric) => (
                  <div
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    ref={provided.innerRef}
                    style={{
                      ...provided.draggableProps.style,
                      display: 'flex',
                      flexDirection: 'column',
                      transform: provided.draggableProps.style?.transform 
                        ? provided.draggableProps.style.transform.replace(/translate\(([^,]+),([^)]+)\)/, (_, x, y) => `translate(${x}, 0px)`)
                        : 'translate(0, 0)',
                      height: 'auto',
                      top: headerRef.current?.getBoundingClientRect().top + 'px',
                      zIndex: 9999,
                      opacity: 0.9,
                      cursor: 'grabbing'
                    }}
                    className="border border-gray-300 bg-gray-100 shadow-lg"
                  >
                    <div className="border-b border-gray-300 p-1 bg-gray-50">
                      <div className="flex items-center justify-center gap-1 h-6">
                        <GripVertical size={14} className="text-gray-600" />
                      </div>
                    </div>
                    <div className="p-2 text-sm font-semibold">
                      {formatColumnName(displayColumns[rubric.source.index])}
                    </div>
                  </div>
                )}
              >
                {(provided) => (
                    <tr 
                    ref={(el) => {
                      headerRef.current = el;
                      provided.innerRef(el);
                    }}
                    className="bg-transparent border-b border-gray-300"
                    {...provided.droppableProps}
                  >
                    {displayColumns.map((columnName, index) => (
                      <Draggable 
                        key={`controls-${columnName}`} 
                        draggableId={columnName} 
                        index={index}
                        isDragDisabled={displayColumns.length <= 1}
                      >
                        {(provided, snapshot) => (
                          <td 
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            ...getDragStyle(snapshot.isDragging, provided.draggableProps.style),
                            position: 'relative',
                          }}
                          className={`border border-gray-300 p-1 text-center
                            ${snapshot.isDragging ? 'bg-gray-100 shadow-lg' : ''}
                            ${snapshot.dropAnimation ? 'transition-all duration-300' : ''}
                            relative w-[180px] max-w-[180px]`}
                        >
                          <div className="flex items-center justify-start px-2 h-8 gap-2">
                            {/* Sort button */}
                            <button
                              onClick={() => {
                                if (sortConfig.key === columnName) {
                                  // If already sorting this column, toggle direction or clear
                                  if (sortConfig.direction === 'asc') {
                                    requestSort(columnName, true); // Switch to desc
                                  } else {
                                    requestSort(columnName); // Clear sort (will set to null)
                                  }
                                } else {
                                  // If not sorting this column, start with asc
                                  requestSort(columnName);
                                }
                              }}
                              className={`p-1.5 rounded hover:bg-gray-100 transition-all flex items-center
                                ${sortConfig.key === columnName 
                                  ? 'bg-accent/10 hover:bg-accent/20' 
                                  : 'hover:bg-gray-100'}`}
                              title={
                                sortConfig.key === columnName 
                                  ? `Sorted ${sortConfig.direction === 'asc' ? 'ascending' : 'descending'} (click to ${sortConfig.direction === 'asc' ? 'reverse' : 'clear'})`
                                  : 'Sort column'
                              }
                            >
                              {sortConfig.key === columnName ? (
                                sortConfig.direction === 'desc' 
                                  ? <ChevronDown size={16} className="text-accent font-bold" strokeWidth={3} />
                                  : <ChevronUp size={16} className="text-accent font-bold" strokeWidth={3} />
                              ) : (
                                <ChevronsUpDown size={16} className="text-gray-400" />
                              )}
                            </button>
                            
                            {/* Filter button */}
                            {originalData.length > 2 && (
                              <button
                                onClick={(e) => handleFilterClick(e, columnName)}
                                className={`p-1.5 rounded transition-all
                                  ${activeFilters[columnName]?.length > 0 
                                    ? 'bg-accent/10 hover:bg-accent/20' 
                                    : 'hover:bg-gray-100'}`}
                                title={activeFilters[columnName]?.length > 0 ? "Filtered" : "Filter column"}
                              >
                                <Filter 
                                  size={16} 
                                  className={activeFilters[columnName]?.length > 0 ? 'text-accent' : 'text-gray-400'}
                                  strokeWidth={activeFilters[columnName]?.length > 0 ? 2.5 : 1.5}
                                />
                              </button>
                            )}
                            
                            {/* Hide button */}
                            {displayColumns.length > 1 && (
                              <button
                                onClick={() => handleHideColumn(columnName)}
                                className="p-1.5 rounded hover:bg-gray-100 transition-all"
                                title="Hide column"
                              >
                                <EyeOff size={16} className="text-gray-400" />
                              </button>
                            )}
                            
                            {/* Drag handle */}
                            {displayColumns.length > 1 && (
                              <span
                                {...provided.dragHandleProps}
                                className={`cursor-grab p-1.5 rounded hover:bg-gray-100 transition-all
                                  ${snapshot.isDragging ? 'cursor-grabbing bg-gray-200' : ''}`}
                                title="Drag to reorder"
                              >
                                <GripVertical size={16} className="text-gray-400" />
                              </span>
                            )}
                          </div>
                        </td>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </tr>
                )}
              </Droppable>
            </DragDropContext>
          </thead>

          {/* Column Headers */}
          <thead 
            className="sticky transition-colors duration-200 bg-gray-100"
            style={{ 
              top: '32px',
              zIndex: 3 
            }}
          >
            <tr>
              {displayColumns.map((columnName) => {
                const stats = showSummary && processedData.length > 1 && !query?.truncationMessage 
                  ? calculateColumnStats(columnName) 
                  : null;
                
                return (
                  <th 
                    key={`header-${columnName}`}
                    className="border border-gray-300 p-2 text-sm font-semibold text-left hover:bg-gray-200 transition-colors align-top"
                    onMouseEnter={stats ? (e) => handleHeaderHover(e, columnName) : undefined}
                    onMouseLeave={stats ? handleHeaderLeave : undefined}
                  >
                    <div className="flex flex-col gap-1">
                      {/* Column name and info icon */}
                      <div className="flex items-center justify-between">
                        <span className="whitespace-normal break-words">
                          {formatColumnNameWithBreaks(columnName)}
                        </span>
                        {columnExplanations[columnName] && (
                          <div className="ml-2 hover:bg-gray-200 hover:rounded-full flex items-center justify-center p-0.5">
                            <ColumnExplanationTooltip
                              columnName={columnName}
                              explanation={columnExplanations[columnName].logic}
                              example={columnExplanations[columnName].example}
                              className="text-blue-700"
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Stats section */}
                      {showSummary && processedData.length > 1 && !query?.truncationMessage && hasColumnsWithStats && (
                        <div className="text-xs font-normal text-gray-500">
                          {stats ? (
                            stats.type === 'numeric' ? (
                              <div>Sum: <span className="text-gray-700">{stats.sum}</span></div>
                            ) : (
                              <div>Unique: <span className="text-gray-700">{stats.unique}</span></div>
                            )
                          ) : (
                            // Empty div to maintain alignment when no stats
                            <div>&nbsp;</div>
                          )}
                        </div>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody className="overflow-y-auto" onMouseUp={handleTextSelection}>
          {processedData.map((row, index) => {
            return (
              <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                {displayColumns.map((columnName) => {
                  // Convert columnName to string to safely use string methods
                  const columnNameStr = String(columnName);
                  const isForecastColumn = columnNameStr.endsWith(' - forecast') || columnNameStr.includes(' - forecast (');
                  const isPrimaryForecast = columnNameStr.endsWith(' - forecast') && !columnNameStr.includes('(');
                  
                  return (
                    <td 
                      key={columnName} 
                      className="border border-gray-300 p-2 text-sm text-left"
                      data-column={columnName}
                      style={isForecastColumn ? { color: '#F59E0B' } : undefined}
                    >
                      {renderCellValue(row[columnName], columnName)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          </tbody>
        </table>
      </div>
    </div>
    <HiddenColumnsChips 
      allColumns={schema?.fields?.map(field => field.name) || []}
      visibleColumns={visibleColumns}
      onShowColumn={handleShowColumn}
      activeFilters={activeFilters}
    />
  </div>
  
      {/* Tooltip */}
        {tooltipContent && processedData.length > 1 && (
          <div
            className="fixed bg-white border border-gray-300 rounded shadow-lg z-[100]"
            style={{
              left: `${tooltipPosition.left + tableRef.current.getBoundingClientRect().left}px`,
              width: `${tooltipPosition.width}px`,
              top: `${tooltipPosition.top + tableRef.current.getBoundingClientRect().top}px`,
              pointerEvents: 'none',
            }}
          >
            {tooltipContent}
            <div 
              className="absolute bottom-0 left-1/2 w-2 h-2 bg-white border-r border-b border-gray-300 transform translate-y-1 rotate-45 -translate-x-1/2"
              style={{ marginBottom: '-1px' }}
            />
          </div>
        )}
  
      {/* Filter Modal */}
      {enableFiltering && (
        <FilterModal 
          isOpen={filterModalState.isOpen}
          onClose={closeFilterModal}
          columnName={filterModalState.columnName}
          data={originalData}
          activeFilters={activeFilters}
          onApplyFilter={handleApplyFilter}
          position={filterModalState.position}
          schema={schema}
          style={{
            maxHeight: '80vh',
            overflowY: 'auto'
          }}
        />
      )}
    </div>
  );
};

TableView.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  originalData: PropTypes.arrayOf(PropTypes.object).isRequired,
  schema: PropTypes.shape({
    fields: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.string
    }))
  }),
  query: PropTypes.shape({
    explanation: PropTypes.shape({
      logic_by_column: PropTypes.objectOf(PropTypes.shape({
        logic: PropTypes.string,
        example: PropTypes.string
      }))
    })
  }),
  initialSortColumn: PropTypes.string,
  initialSortOrder: PropTypes.oneOf(['asc', 'desc']),
  onTextSelection: PropTypes.func,
  visibleColumns: PropTypes.arrayOf(PropTypes.string),
  columnOrder: PropTypes.arrayOf(PropTypes.string),
  onSortChange: PropTypes.func,
  enableFiltering: PropTypes.bool,
  onFilterChange: PropTypes.func,
  onProcessedDataChange: PropTypes.func,
  showSummary: PropTypes.bool,
  query: PropTypes.object,
  isNumberFormatted: PropTypes.bool
};

TableView.defaultProps = {
  visibleColumns: [],
  columnOrder: [],
  enableFiltering: true,
  showSummary: true,
  isNumberFormatted: true
};

export default TableView;